import { useState, useEffect, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { motion } from "framer-motion";
import { Lock, ArrowUpRight } from "lucide-react";
import PrivateHeader from "../../components/private/PrivateHeader";
import Picture from "../../components/Picture";
import SectionLabel from "../../components/SectionLabel";
import { privateApi } from "../../lib/privateApi";
import { devWarn } from "../../lib/devLog";

export default function PrivateIndex() {
    const navigate = useNavigate();
    const [authState, setAuthState] = useState("checking");
    const [email, setEmail] = useState("");
    const [projects, setProjects] = useState([]);
    const [error, setError] = useState("");
    const [filter, setFilter] = useState("all"); // all | private

    // Route guard
    useEffect(() => {
        let cancelled = false;
        privateApi
            .meWithRetry()
            .then((me) => {
                if (cancelled) return;
                setEmail(me.email);
                setAuthState("ok");
            })
            .catch(() => {
                if (cancelled) return;
                navigate("/private/access", { replace: true });
            });
        return () => {
            cancelled = true;
        };
    }, [navigate]);

    // Load projects
    useEffect(() => {
        if (authState !== "ok") return;
        let cancelled = false;
        privateApi
            .projects()
            .then((res) => !cancelled && setProjects(res.items))
            .catch((err) => {
                if (cancelled) return;
                if (err.status === 401) navigate("/private/access", { replace: true });
                else setError(err.message || "Failed to load.");
            });
        return () => {
            cancelled = true;
        };
    }, [authState, navigate]);

    const onSignOut = useCallback(async () => {
        try {
            await privateApi.logout();
        } catch (err) {
            devWarn("private.logout failed; proceeding with client-side sign-out:", err);
        }
        navigate("/private/access", { replace: true });
    }, [navigate]);

    if (authState !== "ok") {
        return (
            <div
                className="min-h-screen bg-[#0B0B0C] flex items-center justify-center"
                data-testid="private-guard-loading"
            >
                <div className="w-32 h-px bg-[rgba(201,168,106,0.3)] relative overflow-hidden">
                    <span className="absolute inset-y-0 left-0 w-1/3 bg-[#C9A86A] animate-[slide_1.2s_ease-in-out_infinite]" />
                </div>
                <style>{`@keyframes slide {0%{transform:translateX(-100%)}100%{transform:translateX(400%)}}`}</style>
            </div>
        );
    }

    const visible = projects.filter((p) =>
        filter === "private" ? p.private_only : true,
    );

    return (
        <div
            className="min-h-screen bg-[#0B0B0C] text-[#F3EFE7]"
            data-testid="private-index-page"
        >
            <Helmet>
                <title>Private Portfolio · Works Held in Confidence — ACT</title>
                <meta name="robots" content="noindex,nofollow" />
            </Helmet>

            <PrivateHeader email={email} onSignOut={onSignOut} />

            {/* Hero band */}
            <section className="bg-[#0B0B0C] pt-16 md:pt-24 pb-14 md:pb-20 border-b border-[rgba(201,168,106,0.12)]">
                <div className="shell">
                    <SectionLabel number="Private" label="Private Collection" />
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-20 mt-8 items-end">
                        <h1 className="lg:col-span-8 font-serif text-5xl md:text-6xl lg:text-7xl leading-[1.04] max-w-4xl">
                            Works held in{" "}
                            <span className="italic text-[#C9A86A]">
                                confidence.
                            </span>
                        </h1>
                        <p className="lg:col-span-4 text-[#9A958A] text-base md:text-lg leading-[1.85] max-w-md lg:pb-2">
                            A deeper record — including three residences never
                            published publicly. All materials are governed by
                            the Agreement you acknowledged at entry.
                        </p>
                    </div>

                    {/* Filter toggle */}
                    <div
                        className="mt-10 md:mt-14 inline-flex items-center gap-0 border border-[rgba(201,168,106,0.25)]"
                        data-testid="private-filter"
                    >
                        {[
                            { key: "all", label: "All Works" },
                            { key: "private", label: "Private Exclusive" },
                        ].map((opt) => {
                            const active = filter === opt.key;
                            return (
                                <button
                                    key={opt.key}
                                    type="button"
                                    onClick={() => setFilter(opt.key)}
                                    className={`px-5 py-2.5 text-[10.5px] tracking-[0.28em] uppercase transition-colors duration-300 ${
                                        active
                                            ? "bg-[#C9A86A]/10 text-[#C9A86A]"
                                            : "text-[#9A958A] hover:text-[#F3EFE7]"
                                    }`}
                                    data-testid={`private-filter-${opt.key}`}
                                >
                                    {opt.label}
                                </button>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* Grid */}
            <section className="bg-[#0B0B0C] py-14 md:py-20">
                <div className="shell">
                    {error && (
                        <div
                            role="alert"
                            className="mb-8 border border-[#C97A6A]/40 bg-[#C97A6A]/5 px-4 py-3 text-[13px] text-[#E0A89A]"
                            data-testid="private-index-error"
                        >
                            {error}
                        </div>
                    )}

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
                        {visible.map((p, idx) => (
                            <motion.div
                                key={p.slug}
                                initial={{ opacity: 0, y: 14 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, amount: 0.15 }}
                                transition={{
                                    duration: 0.7,
                                    delay: (idx % 6) * 0.05,
                                    ease: [0.22, 1, 0.36, 1],
                                }}
                            >
                                <Link
                                    to={`/private/projects/${p.slug}`}
                                    className="group block relative overflow-hidden border border-[rgba(201,168,106,0.15)] hover:border-[rgba(201,168,106,0.5)] transition-all duration-500"
                                    data-testid={`private-card-${p.slug}`}
                                >
                                    <div className="relative aspect-[4/5] overflow-hidden bg-[#1C1D22]">
                                        <Picture
                                            slug={p.cover}
                                            alt={p.name}
                                            sizes="(max-width: 768px) 100vw, 33vw"
                                            className="absolute inset-0 h-full w-full object-cover transition-transform duration-[1400ms] ease-out group-hover:scale-[1.06]"
                                        />
                                        <div className="absolute inset-0 bg-gradient-to-t from-[#0B0B0C] via-[#0B0B0C]/40 to-transparent" />

                                        {p.private_only && (
                                            <span
                                                className="absolute top-5 left-5 inline-flex items-center gap-1.5 border border-[#C9A86A]/60 bg-[#0B0B0C]/70 backdrop-blur-sm text-[10px] tracking-[0.22em] uppercase text-[#C9A86A] px-3 py-1.5"
                                                data-testid={`private-badge-${p.slug}`}
                                            >
                                                <Lock
                                                    size={11}
                                                    strokeWidth={1.6}
                                                />
                                                Private Exclusive
                                            </span>
                                        )}

                                        <div className="absolute inset-x-6 bottom-5">
                                            <span className="text-[10px] tracking-[0.28em] uppercase text-[#9A958A]">
                                                {p.year} · {p.category}
                                            </span>
                                            <h3 className="mt-2 font-serif text-2xl md:text-[26px] text-[#F3EFE7] leading-[1.15] transition-colors duration-500 group-hover:text-[#D9BC80]">
                                                {p.name}
                                            </h3>
                                            <div className="mt-2 flex items-center justify-between gap-3">
                                                <span className="italic text-[#9A958A] text-sm">
                                                    {p.location}
                                                </span>
                                                <span className="inline-flex items-center justify-center w-8 h-8 border border-[#C9A86A]/40 text-[#C9A86A] transition-all duration-500 ease-out group-hover:bg-[#C9A86A] group-hover:text-[#0B0B0C]">
                                                    <ArrowUpRight
                                                        size={13}
                                                        strokeWidth={1.2}
                                                    />
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                </Link>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Private footer */}
            <footer
                className="border-t border-[rgba(201,168,106,0.15)] bg-[#0B0B0C] py-8"
                data-testid="private-footer"
            >
                <div className="shell flex flex-col md:flex-row items-start md:items-center justify-between gap-3">
                    <p className="text-[10.5px] tracking-[0.28em] uppercase text-[#9A958A]">
                        © Advanced Connected Technologies — All material
                        confidential. Do not circulate.
                    </p>
                    <button
                        type="button"
                        onClick={onSignOut}
                        className="text-[10.5px] tracking-[0.28em] uppercase text-[#C9A86A] hover:text-[#D9BC80] transition-colors duration-300"
                        data-testid="private-footer-signout"
                    >
                        Sign Out →
                    </button>
                </div>
            </footer>
        </div>
    );
}
